import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlayerContext } from '../App';
import DevotionalThumbnail from '../components/DevotionalThumbnail';
import { fetchChannelVideos } from '../youtube';

const RADIO_STATIONS = [
  { name: 'FM Chill Lounge',    freq: '91.5 FM', emoji: '🎷', color: 'linear-gradient(135deg,#667eea,#764ba2)' },
  { name: 'Devotional Waves',   freq: '93.1 FM', emoji: '🕉️', color: 'linear-gradient(135deg,#f5a623,#e8722a)' },
  { name: 'News 24/7',          freq: '95.0 FM', emoji: '📰', color: 'linear-gradient(135deg,#2c3e50,#4ca1af)' },
  { name: 'Retro Hits',         freq: '98.3 FM', emoji: '🎸', color: 'linear-gradient(135deg,#f7971e,#ffd200)' },
  { name: 'Classical FM',       freq: '100.7 FM',emoji: '🎻', color: 'linear-gradient(135deg,#c94b4b,#4b134f)' },
  { name: 'Comedy Central Air', freq: '104.5 FM',emoji: '😂', color: 'linear-gradient(135deg,#11998e,#38ef7d)' },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function getGreetingEmoji() {
  const h = new Date().getHours();
  if (h < 12) return '☀️';
  if (h < 17) return '🎵';
  return '🌙';
}

export default function Home() {
  const [videos, setVideos]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [displayCount, setDisplayCount] = useState(60);
  const { currentTrack, setCurrentTrack, setQueue, setQueueIdx } = useContext(PlayerContext);
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const view  = params.get('view') || 'home';
  const query = params.get('q')    || '';

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchChannelVideos()
      .then(data => setVideos(data))
      .catch(e => setError('Could not load music. ' + (e.message || '')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem('fm_recent') || '[]');
      setRecentlyPlayed(data.filter(t => t.ytId));
    } catch {}
  }, [currentTrack]);

  const playVideo = (video, allVideos) => {
    const vids = allVideos || videos;
    const idx  = vids.findIndex(v => v.id === video.id);
    setQueue(vids);
    setQueueIdx(idx >= 0 ? idx : 0);
    setCurrentTrack(video);
  };

  const resumeTrack = (track) => {
    const idx = videos.findIndex(v => v.id === track.id);
    if (idx >= 0) {
      setQueue(videos);
      setQueueIdx(idx);
      setCurrentTrack(videos[idx]);
    } else {
      setQueue([track]);
      setQueueIdx(0);
      setCurrentTrack(track);
    }
  };

  /* ── Search ── */
  if (view === 'search' && query) {
    const q       = query.toLowerCase();
    const matched = videos.filter(v =>
      v.title.toLowerCase().includes(q) || v.description?.toLowerCase().includes(q)
    );
    return (
      <div className="home">
        <div className="page-header"><h1 className="page-title">🔍 "{query}"</h1></div>
        <div className="am-section" style={{ paddingBottom: 32 }}>
          <div className="am-section-header">
            <h2>Music</h2>
            <span className="am-see-all">{matched.length} found</span>
          </div>
          {matched.length === 0
            ? <div className="empty-state"><div className="empty-icon">🔍</div><p>No tracks match "{query}"</p></div>
            : <div className="browse-grid">
                {matched.map(v => (
                  <VideoTile key={v.id} video={v}
                    onPlay={(e) => { e.stopPropagation(); playVideo(v, matched); }}
                    onClick={() => navigate(`/show/${v.id}`)} />
                ))}
              </div>
          }
        </div>
      </div>
    );
  }

  /* ── Radio ── */
  if (view === 'radio') {
    return (
      <div className="home">
        <div className="page-header"><h1 className="page-title">📻 Radio</h1></div>
        <div className="am-section" style={{ paddingBottom: 32 }}>
          <div className="am-section-header">
            <h2>Live Stations</h2>
            <span className="am-see-all">{RADIO_STATIONS.length} stations</span>
          </div>
          <div className="browse-grid">
            {RADIO_STATIONS.map((station, i) => (
              <div key={i} className="browse-tile">
                <div className="browse-tile-fallback" style={{ background: station.color }}>
                  {station.emoji}
                </div>
                <div className="browse-tile-overlay" />
                <div className="browse-tile-content">
                  <div className="browse-tile-cat">{station.freq}</div>
                  <div className="browse-tile-title">{station.name}</div>
                </div>
                <div className="live-badge">LIVE</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── New ── */
  if (view === 'new') {
    const newVids = videos.slice(0, 10);
    return (
      <div className="home">
        <div className="page-header"><h1 className="page-title">✨ New</h1></div>
        <div className="am-section" style={{ paddingBottom: 32 }}>
          <div className="am-section-header"><h2>Newly Added</h2><span className="am-see-all">{newVids.length} tracks</span></div>
          {loading ? <SkeletonGrid /> : newVids.length === 0 ? <EmptyState error={error} /> :
            <div className="browse-grid">
              {newVids.map(v => (
                <VideoTile key={v.id} video={v}
                  onPlay={(e) => { e.stopPropagation(); playVideo(v); }}
                  onClick={() => navigate(`/show/${v.id}`)} />
              ))}
            </div>
          }
        </div>
      </div>
    );
  }

  /* ── Recently Added ── */
  if (view === 'recent') {
    return (
      <div className="home">
        <div className="page-header"><h1 className="page-title">🕐 Recently Added</h1></div>
        <div className="am-section" style={{ paddingBottom: 32 }}>
          <div className="am-section-header"><h2>Latest Tracks</h2><span className="am-see-all">{videos.length} tracks</span></div>
          {loading ? <SkeletonGrid /> : videos.length === 0 ? <EmptyState error={error} /> :
            <div className="browse-grid">
              {videos.map(v => (
                <VideoTile key={v.id} video={v}
                  onPlay={(e) => { e.stopPropagation(); playVideo(v); }}
                  onClick={() => navigate(`/show/${v.id}`)} />
              ))}
            </div>
          }
        </div>
      </div>
    );
  }

  /* ── All Music ── */
  if (view === 'music') {
    return (
      <div className="home">
        <div className="page-header"><h1 className="page-title">🎵 All Music</h1></div>
        <div className="am-section" style={{ paddingBottom: 32 }}>
          <div className="am-section-header"><h2>All Tracks</h2><span className="am-see-all">{videos.length} tracks</span></div>
          {loading ? <SkeletonGrid /> : videos.length === 0 ? <EmptyState error={error} /> :
            <>
              <div className="browse-grid">
                {videos.slice(0, displayCount).map(v => (
                  <VideoTile key={v.id} video={v}
                    onPlay={(e) => { e.stopPropagation(); playVideo(v); }}
                    onClick={() => navigate(`/show/${v.id}`)} />
                ))}
              </div>
              {displayCount < videos.length && (
                <div style={{ textAlign: 'center', padding: '28px 0 8px' }}>
                  <button className="hero-btn-primary" onClick={() => setDisplayCount(c => c + 60)}>
                    Load More ({videos.length - displayCount} remaining)
                  </button>
                </div>
              )}
            </>
          }
        </div>
      </div>
    );
  }

  /* ── Home (default) ── */
  return (
    <div className="home">

      {/* ── Hero greeting ── */}
      <div className="home-hero">
        <div className="home-hero-text">
          <div className="home-hero-emoji">{getGreetingEmoji()}</div>
          <h1 className="home-hero-title">{getGreeting()}</h1>
          <p className="home-hero-sub">What do you want to listen to?</p>
        </div>
        <a href="https://www.youtube.com/@ShriTvSongs" target="_blank" rel="noopener noreferrer" className="upload-link">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6v2H5v11h11v-5h2v6a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1h6zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8z"/>
          </svg>
          YouTube
        </a>
      </div>

      {/* ── Continue Listening ── */}
      {recentlyPlayed.length > 0 && (
        <div className="am-section">
          <div className="am-section-header">
            <h2>Continue Listening</h2>
            <button className="am-see-all" onClick={() => {
              try { localStorage.removeItem('fm_recent'); setRecentlyPlayed([]); } catch {}
            }}>Clear</button>
          </div>
          <div className="am-scroll">
            {recentlyPlayed.map(track => {
              const isPlaying = currentTrack?.id === track.id;
              return (
                <div key={track.id} className="recent-card" onClick={() => resumeTrack(track)}>
                  <div className="recent-card-art">
                    <DevotionalThumbnail id={track.id} showTitle={false} />
                    {isPlaying && (
                      <div className="recent-card-playing">
                        <div className="eq-bars"><span/><span/><span/><span/></div>
                      </div>
                    )}
                  </div>
                  <div className="recent-card-title">{track.title}</div>
                  <div className="recent-card-show">{track.showTitle}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Top Picks horizontal shelf ── */}
      <div className="am-section">
        <div className="am-section-header">
          <h2>Top Picks for You</h2>
          <button className="am-see-all" onClick={() => navigate('/?view=music')}>See All</button>
        </div>
        {loading
          ? <SkeletonScroll />
          : videos.length > 0 && (
            <div className="am-scroll">
              {videos.slice(0, 10).map(v => (
                <div key={v.id} className="featured-card" onClick={() => navigate(`/show/${v.id}`)}>
                  <div className="featured-card-art">
                    <DevotionalThumbnail id={v.id} title={v.title} />
                    <button className="featured-card-play" onClick={(e) => { e.stopPropagation(); playVideo(v); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div>

      {/* ── Browse all music grid ── */}
      <div className="am-section" style={{ paddingBottom: 40 }}>
        <div className="am-section-header">
          <h2>Browse All Music</h2>
          <span className="am-see-all">{videos.length} tracks</span>
        </div>
        {loading
          ? <SkeletonGrid />
          : videos.length === 0
            ? <EmptyState error={error} />
            : <>
                <div className="browse-grid">
                  {videos.slice(0, displayCount).map(v => (
                    <VideoTile key={v.id} video={v}
                      onPlay={(e) => { e.stopPropagation(); playVideo(v); }}
                      onClick={() => navigate(`/show/${v.id}`)} />
                  ))}
                </div>
                {displayCount < videos.length && (
                  <div style={{ textAlign: 'center', padding: '28px 0 8px' }}>
                    <button className="hero-btn-primary" onClick={() => setDisplayCount(c => c + 60)}>
                      Load More ({videos.length - displayCount} remaining)
                    </button>
                  </div>
                )}
              </>
        }
      </div>
    </div>
  );
}

/* ── Video tile — always shows devotional art ── */
function VideoTile({ video, onPlay, onClick }) {
  return (
    <div className="browse-tile" onClick={onClick}>
      <DevotionalThumbnail id={video.id} title={video.title} />
      <button className="browse-tile-play" onClick={onPlay}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg>
      </button>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="browse-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="browse-tile skeleton-tile">
          <div className="skeleton-shimmer" />
        </div>
      ))}
    </div>
  );
}

function SkeletonScroll() {
  return (
    <div className="am-scroll">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="featured-card">
          <div className="featured-card-art skeleton-tile">
            <div className="skeleton-shimmer" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ error }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">🎵</div>
      {error
        ? <p style={{ color: 'var(--accent)' }}>{error}</p>
        : <p>No tracks found.</p>}
    </div>
  );
}
